
import { Component, Vue, Watch } from 'vue-property-decorator';
import { UserRepoInterface, UserInterface, UsersTableInterface } from '@/services/Users/Interfaces';
import DeleteUserDialog from '@/components/dialogs/users/DeleteUserDialog.vue';
import { DataTableHeader } from 'vuetify';
import ChangeUsersRoleDialog from '@/components/dialogs/users/ChangeUsersRoleDialog.vue';
import { ProfileRepositoryInterface } from '@/services/Users-profile/interfaces';
import PageHeader from '@/components/layout/PageHeader.vue';
import {
  LIST_FOOTER_PROPS,
  ListPaginationOptions,
  buildListPaginationQuery,
  defaultListOptions,
  effectivePageSize,
  listPaginationQueryEquals,
  parseListQuery,
} from '@/utils/listPagination';

@Component({
  components: {
    DeleteUserDialog,
    ChangeUsersRoleDialog,
    PageHeader,
  },
  inject: [
    'userRepository',
    'profileRepository',
  ],
})
export default class UserIndex extends Vue {
  private loading: boolean = true;
  private search: string = '';
  private users: UsersTableInterface[] = [];

  public LIST_FOOTER_PROPS = LIST_FOOTER_PROPS;

  private options: ListPaginationOptions = defaultListOptions();
  private totalItems: number = 0;

  private ignoreRouteSync = false;
  private applyingRouteQuery = false;

  private headers: DataTableHeader[] = [
    { text: 'Username', value: 'username' },
    { text: 'Email', value: 'email' },
    { text: 'Name', value: 'name' },
    { text: 'Department', value: 'department' },
    { text: 'Role', value: 'role' },
    { text: 'Deleted', value: 'deletedAt' },
    { text: 'Actions', value: 'actions', sortable: false },
  ];

  private deleteUserForDialog: UserInterface | false = false;
  private deleteUserDialog: string = '';

  private selectedUsers: UserInterface[] | false = [];
  private changeUsersRoleDialog: string = '';

  private readonly userRepository!: UserRepoInterface;
  private readonly profileRepository!: ProfileRepositoryInterface;

  private created(): void {
    this.applyRouteQuery();
    this.initialize();
  }

  public applyRouteQuery(): void {
    this.applyingRouteQuery = true;
    try {
      this.options = parseListQuery(this.$route.query);
      this.$nextTick(() => {
        this.applyingRouteQuery = false;
      });
    } catch (err) {
      this.applyingRouteQuery = false;
      throw err;
    }
  }

  public buildListQuery(): { [key: string]: string } {
    return buildListPaginationQuery(this.options);
  }

  public listQueryEquals(query: { [key: string]: string | (string | null)[] | null | undefined }): boolean {
    return listPaginationQueryEquals(query, this.options);
  }

  public async syncRouteQuery(): Promise<void> {
    if (this.ignoreRouteSync || this.applyingRouteQuery) {
      return;
    }

    const query = this.buildListQuery();
    if (this.listQueryEquals(this.$route.query)) {
      return;
    }

    this.ignoreRouteSync = true;
    try {
      await this.$router.replace({ name: 'users_index', query });
    } catch (err) {
      // Ignore NavigationDuplicated.
    } finally {
      this.$nextTick(() => {
        this.ignoreRouteSync = false;
      });
    }
  }

  public async onSearch(): Promise<void> {
    this.options.page = 1;
    await this.syncRouteQuery();
    await this.initialize();
  }

  public async onPageChange(): Promise<void> {
    this.loading = true;
    try {
      await this.syncRouteQuery();
      await this.initialize();
    } finally {
      this.loading = false;
    }
  }

  public async onItemsPerPageChange(): Promise<void> {
    this.options.page = 1;
    this.loading = true;
    try {
      await this.syncRouteQuery();
      await this.initialize();
    } finally {
      this.loading = false;
    }
  }

  @Watch('$route')
  public async onRouteChange(): Promise<void> {
    if (this.ignoreRouteSync) {
      return;
    }
    if (this.$route.name !== 'users_index') {
      return;
    }
    if (this.listQueryEquals(this.$route.query)) {
      return;
    }

    this.applyRouteQuery();
    await this.initialize();
  }

  private async initialize(): Promise<void> {
    this.loading = true;
    this.users = [];
    const [users, count] = await this.userRepository.getAllUsers(
      this.options.page,
      this.search,
      effectivePageSize(this.options.itemsPerPage),
    );
    this.users = users as UsersTableInterface[];
    this.totalItems = count;

    const profiles = await this.profileRepository.getBaseProfileDataByUsersIds(users.map((user) => String(user.id)));
    profiles.forEach((profile) => {
      const userIndex = this.users.findIndex((user) => String(user.id) === String(profile.userId));
      if (userIndex !== -1) {
        this.users[userIndex].name = `${profile.firstName} ${profile.lastName}`;
      }
    });

    this.users.map((user: UserInterface) => {
      user.deletedAt = user.deletedAt ? user.deletedAt : 'false';
      return user;
    });

    this.loading = false;
  }

  private createUser(): void {
    this.$router.push({ name: 'user_create' });
  }

  private editUser(user: UserInterface): void {
    this.$router.push({ name: 'user_edit', params: { userId: user.id } });
  }

  private deleteUser(user: UserInterface): void {
    this.deleteUserDialog = 'delete-user-dialog';
    this.deleteUserForDialog = user;
  }

  private async forceLogoutUser(user: UserInterface): Promise<void> {
    await this.userRepository.forceLogout(user.id);
  }

  private async changeRoleOfSelected(): Promise<void> {
    this.changeUsersRoleDialog = 'change-users-role-dialog';
  }

}
